import React, { useRef, useState } from 'react';
import classNames from 'classnames';

import css from './ChipInput.module.css';

/**
 * Chip / tag input for free-form short string lists.
 *
 * The user types a value, presses Enter or comma to commit it as a
 * chip, and clicks the × on a chip to remove it. The input is the
 * uncontrolled textbox; the chips are the persistent value passed
 * via `value` and updated via `onChange(newValueArray)`.
 *
 * Used in Phase D for the listing-creation Tags section but kept
 * generic so the same component can back any future chip field
 * (skills, dietary tags, etc.).
 *
 * @component
 * @param {Object} props
 * @param {string[]} props.value - Current array of chips.
 * @param {(value: string[]) => void} props.onChange - Called whenever
 *   chips are added or removed. Receives the full new array.
 * @param {string} [props.placeholder] - Placeholder for the textbox
 *   when below the cap; auto-replaced with a "max reached" message
 *   when full.
 * @param {number} [props.maxItems=10] - Cap on chip count. The input
 *   is disabled and given a hint state once reached.
 * @param {number} [props.maxItemLength=30] - Per-chip character cap.
 *   Longer entries get truncated on add.
 * @param {string} [props.className] - Optional class on the outer
 *   wrapper (root .chipInput).
 * @param {string} [props.id] - DOM id for the textbox; used for
 *   <label htmlFor> wiring on the form side.
 */
const ChipInput = props => {
  const {
    value = [],
    onChange,
    placeholder,
    maxItems = 10,
    maxItemLength = 30,
    className,
    id,
  } = props;

  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const isFull = value.length >= maxItems;

  // Normalize an incoming chip string. Empty result means "reject".
  const normalize = raw => {
    if (typeof raw !== 'string') return '';
    return raw
      .trim()
      .toLowerCase()
      .slice(0, maxItemLength);
  };

  const commitDraft = () => {
    const next = normalize(draft);
    setDraft('');
    if (!next) return;
    if (value.includes(next)) return;       // dedupe
    if (value.length >= maxItems) return;   // cap
    onChange([...value, next]);
  };

  const removeAt = idx => {
    const updated = value.filter((_, i) => i !== idx);
    onChange(updated);
    // Return focus to the input so users can keep typing without
    // re-clicking it.
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') {
      // Prevent form submit / literal comma in the input.
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      // Backspace on empty input removes the last chip — common
      // chip-input convention. No event.preventDefault since the
      // textbox is already empty.
      removeAt(value.length - 1);
    }
  };

  const handleBlur = () => {
    // Commit any partial chip when the user tabs / clicks away — common
    // chip-input convention. If the draft is rejected (empty/dupe/cap),
    // commitDraft just clears it.
    if (draft) commitDraft();
  };

  const effectivePlaceholder = isFull
    ? `Max ${maxItems} reached`
    : placeholder || 'Type and press Enter';

  return (
    <div className={classNames(css.chipInput, className)}>
      <div className={css.chips}>
        {value.map((chip, i) => (
          <span key={`${chip}-${i}`} className={css.chip}>
            <span className={css.chipText}>{chip}</span>
            <button
              type="button"
              className={css.chipRemove}
              onClick={() => removeAt(i)}
              aria-label={`Remove ${chip}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={css.input}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isFull}
          placeholder={effectivePlaceholder}
          maxLength={maxItemLength}
        />
      </div>
      <div className={css.counter}>
        {value.length} / {maxItems}
      </div>
    </div>
  );
};

export default ChipInput;
